import { useEffect, useState } from "react";
import AmenityIconRenderer from "@/components/tpr/AmenityIconRenderer/AmenityIconRenderer";
import BigHeading from "@/components/tpr/Heading/BigHeader";
import Heading from "@/components/tpr/Heading/Heading";
import InlineMemberCard from "@/components/tpr/InlineMemberCard/InlineMemberCard";
import SimpleBookingsList from "@/components/tpr/SimpleBookingsList/SimpleBookingsList";
import LocationNotification from "@/components/tpr/LocationNotifications/LocationNotification";
import RoomsGrid from "@/components/tpr/RoomsGrid";
import { BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { LocationService } from "@/services/LocationService";
import { Accessibility, CalendarDays, ChevronDownIcon, ChevronLeft, Info, MapPin, Send } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMessagesEnabled } from "@/hooks/useMessagesEnabled";
import { useOrderedRooms } from "@/hooks/useOrderedRooms";
import type { Location } from "@/types/Location";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn, getInitials, getLocationLabel } from "@/lib/utils";

const LocationView = () => {
  const messagesEnabled = useMessagesEnabled();
  const { setBreadcrumb } = useBreadcrumb();
  let navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawReturnUrl = searchParams.get("returnUrl");
  const returnUrl = rawReturnUrl && rawReturnUrl.startsWith("/") && !rawReturnUrl.startsWith("//") ? rawReturnUrl : null;

  const [location, setLocation] = useState<Location | undefined>(undefined);
  const [locations, setLocations] = useState<Location[]>([]);
  const [practiceManagers, setPracticeManagers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Directions is only a Google Maps link, so it collapses by default to stay compact.
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const params = useParams();
  const { data: orderedRooms } = useOrderedRooms(params.locationSlug);

  const handleLocationSelect = (slug: string) => {
    console.log("handleLocationSelect", slug);
    navigate(`/location/${slug}`);
  };

  useEffect(() => {
    setLoading(true);

    const getData = async () => {
      if (params.locationSlug) {
        const locationService = new LocationService();

        const _location = await locationService.getLocationById(params.locationSlug);
        const _locations = await locationService.getPermissableLocations();
        const _practiceManagers = await locationService.getPracticeManagerByLocation(params.locationSlug);

        setLocation(_location || undefined);
        setLocations(_locations);
        setPracticeManagers(_practiceManagers || []);
        setLoading(false);
      }
    };
    getData();
  }, [params.locationSlug]);

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5">
              Locations <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {locations.map((l) => (
                <DropdownMenuItem key={l.slug}>
                  <BreadcrumbLink onClick={() => handleLocationSelect(l.slug)}>{l.name}</BreadcrumbLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="text-sm text-gray-600 truncate overflow-hidden max-w-[70px] md:max-w-[120px] lg:max-w-none">
          <BreadcrumbLink href="#">
            {location ? getLocationLabel(location) : "Location"}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [location, setBreadcrumb, locations, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid flex items-center justify-center">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading location...</span>
      </div>
    );
  }

  const renderAddress = (location: Location) => {
    if (location.googleMapsUrl) {
      return (
        <a
          href={location.googleMapsUrl}
          className="flex flex-col hover:text-blue-700"
          target="_blank"
          rel="noopener noreferrer"
          title="Open map"
        >
          <span className="underline decoration-dotted decoration-gray-300">{location?.address?.line1 || null}</span>
          <span className="underline decoration-dotted decoration-gray-300">{location?.address?.line2 || null}</span>
          <span className="underline decoration-dotted decoration-gray-300">{location?.address?.city || null}</span>
          <span className="underline decoration-dotted decoration-gray-300">{location?.address?.postcode || null}</span>
        </a>
      );
    } else {
      return (
        <div className="flex flex-col">
          <span>{location?.address?.line1 || null}</span>
          <span>{location?.address?.line2 || null}</span>
          <span>{location?.address?.city || null}</span>
          <span>{location?.address?.postcode || null}</span>
        </div>
      );
    }
  };

  return (
    <>
      <div>
        <div className="px-4 mb-8">
          <div className="mb-8 mt-8">
            {location && (
              <div className="flex justify-center mb-5">
                <Avatar className="w-24 h-24 md:w-36 md:h-36 lg:w-48 lg:h-48 border-4 border-white shadow-md">
                  <AvatarImage
                    src={location.locationImageUrl}
                    alt={location.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="bg-gray-100 text-gray-600 text-2xl md:text-4xl lg:text-5xl font-semibold">
                    {location.initials || getInitials(location.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
            )}
            <BigHeading title={location ? getLocationLabel(location) : ""} />
          </div>

          {returnUrl && (
            <div className="w-full bg-slate-300 rounded-md p-2 mb-4 flex justify-between">
              <div className="flex flex-row items-center">
                <Button
                  variant="link"
                  onClick={() => navigate(returnUrl)}
                  className="text-base text-slate-800 hover:text-blue-800 hover:cursor-pointer"
                >
                  <ChevronLeft />
                  Go back
                </Button>
              </div>
            </div>
          )}

          {/* Location information wells — the centre blurb, accessibility and directions. Shown ABOVE
              the address/door-code section (per operator request 2026-07-14): the descriptive intro
              first, then the practical details. About + Accessibility sit two-up; Directions is only a
              link so it gets its own compact, collapsible row underneath. Only rendered when at least
              one field is set. */}
          {(location?.description || location?.accessibility || location?.googleMapsUrl) && (
            <div className="mb-8" data-testid="location-info-wells">
              {(location?.description || location?.accessibility) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {location?.description && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3 dark:bg-gray-800 dark:border-gray-700">
                      <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">About this centre</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{location.description}</p>
                      </div>
                    </div>
                  )}
                  {location?.accessibility && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3 dark:bg-gray-800 dark:border-gray-700">
                      <Accessibility className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Accessibility</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{location.accessibility}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {location?.googleMapsUrl && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setDirectionsOpen((v) => !v)}
                    aria-expanded={directionsOpen}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    <span className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Directions</span>
                    </span>
                    <ChevronDownIcon
                      className={cn("w-4 h-4 text-gray-500 transition-transform", directionsOpen && "rotate-180")}
                    />
                  </button>
                  {directionsOpen && (
                    <div className="px-4 pb-4 pl-12">
                      <a
                        href={location.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                      >
                        View on Google Maps
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            <div className="xl:col-span-2">
              <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700">
                {/* <div
                  className="w-full rounded-lg mb-4 bg-center bg-cover"
                  style={{
                    backgroundImage: location?.locationImageUrl ? `url(${location.locationImageUrl})` : undefined,
                    height: "240px",
                  }}
                  aria-label={location?.name}
                /> */}

                {/* <VideoBlock videoUrl={location?} /> */}

                <div className="px-5 mt-4">
                  <dl className="text-gray-900 divide-y divide-gray-200 dark:text-white dark:divide-gray-700">
                    <div className="flex flex-row pb-3">
                      <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">Address</dt>
                      <dd className="text-base font-semibold">
                        {location ? renderAddress(location) : "No address available"}
                      </dd>
                    </div>
                    {practiceManagers && practiceManagers.length > 0 && (
                      <div className="flex flex-row pb-3 pt-3">
                        <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">
                          Practice Manager{practiceManagers.length > 1 ? "s" : ""}
                        </dt>
                        <dd className="text-base font-semibold flex flex-col lg:flex-row gap-4">
                          {practiceManagers.map((manager: any) => (
                            <InlineMemberCard
                              key={manager.id}
                              id={manager.id}
                              firstName={manager.firstName}
                              lastName={manager.lastName}
                              email={manager.email}
                              avatar={manager.avatar}
                              active={manager.active ?? true}
                              permissableLocations={manager.permissableLocations}
                              introduction={manager.introduction}
                              showMessageButton
                            />
                          ))}
                        </dd>
                      </div>
                    )}
                    <div className="flex flex-row py-3">
                      <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">Door code</dt>
                      <dd className="text-base font-semibold">{location?.doorCode || "No door code available"}</dd>
                    </div>
                    <div className="flex flex-row py-3">
                      <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">Wifi</dt>
                      <dd className="text-base font-semibold overflow-hidden">
                        <span className="flex flex-col lg:flex-row  text-sm lg:text-base">
                          <span className="font-semibold mr-4 text-gray-500">Network</span>
                          {location?.wifi?.network ? location.wifi.network : "No wifi network available"}
                        </span>
                        <span className="flex flex-col lg:flex-row text-sm lg:text-base">
                          <span className="font-semibold mr-4 text-gray-500">Password</span>
                          {location?.wifi?.password ? location.wifi.password : "No wifi password available"}
                        </span>
                      </dd>
                    </div>
                    <div className="flex flex-row py-3">
                      <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">
                        Is accessible?
                      </dt>
                      <dd className="text-base font-semibold">{location?.isAccessible ? "Yes" : "No"}</dd>
                    </div>
                    <div className="flex flex-row py-3">
                      <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 w-38 min-w-38">Amenities</dt>
                      <dd className="text-base font-semibold flex flex-col gap-2">
                        {location?.amenities && location.amenities.filter((a) => !a.hidden).length > 0
                          ? location.amenities
                              .filter((a) => !a.hidden)
                              .map((amenity) => {
                                return <AmenityIconRenderer key={amenity.id} amenity={amenity} withDescription />;
                              })
                          : "No amenities available"}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-8">
                {messagesEnabled && (
                <Button
                  className="mt-2 w-full hover:bg-indigo-700 hover:text-white"
                  variant="secondary"
                  onClick={() => {
                    if (practiceManagers && practiceManagers.length > 0) {
                      const firstPM = practiceManagers[0];
                      navigate("/messages", {
                        state: {
                          recipient: {
                            type: "member",
                            id: firstPM.id,
                            firstName: firstPM.firstName,
                            lastName: firstPM.lastName,
                            email: firstPM.email,
                            avatar: firstPM.avatar,
                            active: firstPM.active,
                          },
                        },
                      });
                    }
                  }}
                  disabled={!practiceManagers || practiceManagers.length === 0}
                >
                  <Send /> Contact the Practice Manager
                </Button>
                )}
                <Button
                  className="mt-2 w-full hover:bg-indigo-700 hover:text-white"
                  variant="secondary"
                  onClick={() => navigate(`/location/${location?.slug}/calendar`, { replace: true })}
                >
                  <CalendarDays /> View Calendar
                </Button>
              </div>

              <div className="mb-8 mt-8 md:mt-0">
                <Heading title={`Practice Notices`} />
              </div>
              <ScrollArea className="max-h-[400px]">
                <LocationNotification location={location!} />
              </ScrollArea>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            <div className="xl:col-span-2">
              <div className="mb-8 mt-8">
                <Heading title="Our Rooms" />
              </div>
              <RoomsGrid rooms={orderedRooms ?? []} locationSlug={location?.slug || ""} />
            </div>

            <div>
              <div className="mb-8 mt-8">
                <Heading title={`Your bookings`} />
              </div>
              <p className="text-center mb-4 text-gray-700 text-sm">Showing the next 10 bookings at this location</p>
              <SimpleBookingsList
                locationId={location?.id}
                returnUrl={location?.slug ? `/location/${location.slug}` : "/dashboard"}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationView;
